import type { MouseEventHandler } from "react";
import React, { useMemo } from "react";

import cn from "classnames";

import { Box, boxForwardRef } from "../../../helpers/common/Box/Box";
import { useMouseDownAnimation } from "../../../hooks/useMouseDownAnimation";
import { getChildrenRecord } from "../../../utils/react";
import { Badge } from "../../Badge/Badge";

import type { ChipProps } from "./types";
import type { BoxComponentWithDefault } from "../../../helpers/common/Box/Box";

import classes from "./ChipUser.styl";

const CHIP_USER_DISPLAY_NAME = "ChipUser";

/**
 * Чипс для выбора/тэга пользователей.
 *
 * В качестве `as` ожидаем, что будет передан 'button', 'a' или компонент, реализующий функцию навигации (например, `RoutingLink` на большом вебе).
 * Более подробную информацию можно найти [тут](/docs/документация-разработчикам-дс-инструкция-по-разработке-полиморфных-компонентов--docs).
 *
 * <a href="https://www.figma.com/design/LYZAXE33pUg2cSL5mmUHYq/%F0%9F%8D%91-Buttons-%26-Tabs-%26-Chips?m=auto&node-id=2-2631&t=3ryRFv5yEABAv4kK-1" target="_blank">Спека</a>
 * @constructor
 */

export const ChipUser = boxForwardRef<
    BoxComponentWithDefault<"button", ChipProps>
>(
    (
        {
            as = "button",
            customColorsStyles,
            view = "primary",
            selected = false,
            imageSrc,
            alt,
            onClick,
            "data-tsid": tsid,
            children,
            onMouseDown,
            ...props
        },
        ref,
    ) => {
        const isSelected = selected;

        const { Label, Icon } = useMemo(
            () =>
                getChildrenRecord(children, "ChipUser", {
                    Label: {},
                    Icon: {
                        props: {
                            size: "16",
                            icon: "glyph_close_16_20",
                        },
                    },
                }),
            [children],
        );

        const { clicked, mousedownHandler } = useMouseDownAnimation();

        const mouseDownHandler: MouseEventHandler<HTMLElement> = (e) => {
            mousedownHandler();
            onMouseDown?.(e);
        };

        const containerClassName = cn(
            classes["chip-container"],
            classes[`__view-${view}`],
            {
                [classes[`__selected-${view}`]]: isSelected,
            },
            {
                [classes[`__clicked`]]: clicked,
            },
            customColorsStyles,
        );

        return (
            <Box
                as={as}
                {...props}
                type="button"
                className={containerClassName}
                data-tsid={tsid}
                data-uikit={CHIP_USER_DISPLAY_NAME}
                onClick={onClick}
                onMouseDown={mouseDownHandler}
                ref={ref}
            >
                <span className={classes["badge"]}>
                    <Badge content="image" src={imageSrc} alt={alt} size="24" />
                </span>
                <span>{Label}</span>
                {isSelected && <span className={classes["icon"]}>{Icon}</span>}
            </Box>
        );
    },
);

ChipUser.displayName = CHIP_USER_DISPLAY_NAME;

export default ChipUser;
